<template>
  <div class="flex flex-col items-center px-2 rounded-lg hover:bg-white/5 transition-colors cursor-pointer">
    <NuxtLink
      to="/agent"
      class="relative flex flex-col items-center justify-center py-1"
      :title="t('workflows.title')"
      @click.stop="toggleDropdown"
    >
      <div class="relative">
        <Icon name="heroicons:arrow-up-tray" class="w-6 h-6 text-white" />
        <!-- Badge avec le nombre de workflows -->
        <div
          v-if="workflowCount > 0"
          class="absolute -top-1 -right-2 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center shadow-lg"
          :class="{ 'animate-pulse': newWorkflowsCount > 0 }"
        >
          {{ workflowCount > 9 ? '9+' : workflowCount }}
        </div>
      </div>
      <span class="text-xs font-bold mt-1 text-white leading-none">Upload</span>

      <!-- Badge "En cours" pour workflows actifs -->
      <span
        v-if="hasActiveWorkflows"
        class="absolute top-0 right-0 bg-green-400 rounded-full h-2.5 w-2.5 animate-pulse shadow-lg"
      />
    </NuxtLink>

    <!-- Bulle de notification (à droite du bouton) -->
    <Transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="transform opacity-0 translate-x-2"
      enter-to-class="transform opacity-100 translate-x-0"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="transform opacity-100 translate-x-0"
      leave-to-class="transform opacity-0 translate-x-2"
    >
      <div
        v-if="newWorkflowsCount > 0"
        class="absolute right-full top-1/2 -translate-y-1/2 mr-3 bg-teal-500 text-white px-3 py-1.5 rounded-lg shadow-lg text-sm font-medium whitespace-nowrap flex items-center space-x-2 animate-pulse"
      >
        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
        </svg>
        <span>
          {{ newWorkflowsCount }} {{ newWorkflowsCount > 1 ? t('workflows.filesCompleted') : t('workflows.fileCompleted') }}
        </span>
      </div>
    </Transition>

    <!-- Dropdown -->
    <Transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="showDropdown"
        class="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-hidden"
      >
        <!-- Header -->
        <div class="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 class="text-sm font-semibold text-gray-900">
            {{ t('workflows.title') }}
          </h3>
          <span class="text-xs text-gray-500">
            {{ workflowCount }} {{ workflowCount > 1 ? t('workflows.workflows') : t('workflows.workflow') }}
          </span>
        </div>

        <!-- Liste des workflows -->
        <div class="max-h-80 overflow-y-auto">
          <div v-if="workflows.length === 0" class="px-4 py-8 text-center">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p class="mt-2 text-sm text-gray-500">{{ t('workflows.noWorkflows') }}</p>
          </div>

          <div
            v-for="workflow in workflows"
            :key="workflow.id"
            @click="goToWorkflow(workflow.id)"
            class="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0"
          >
            <div class="flex items-start space-x-3">
              <!-- Icône de statut -->
              <div class="flex-shrink-0 mt-0.5">
                <div
                  class="w-10 h-10 rounded-lg flex items-center justify-center"
                  :class="{
                    'bg-blue-100': workflow.status === 'processing',
                    'bg-green-100': workflow.status === 'completed' || workflow.status === 'error',
                    'bg-gray-100': workflow.status === 'pending'
                  }"
                >
                  <!-- En cours -->
                  <svg v-if="workflow.status === 'processing'" class="w-5 h-5 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <!-- Terminé -->
                  <svg v-else-if="workflow.status === 'completed' || workflow.status === 'error'" class="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  </svg>
                  <!-- Erreur -->
                  <!--                   <svg v-else-if="workflow.status === 'error'" class="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                  </svg> -->
                  <!-- En attente -->
                  <svg v-else class="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd" />
                  </svg>
                </div>
              </div>

              <!-- Informations -->
              <div class="flex-1 min-w-0">
                <div class="flex items-center space-x-2">
                  <p class="text-sm font-medium text-gray-900 truncate" :title="workflow.filename">
                    {{ workflow.filename }}
                  </p>
                  <!-- Badge NOUVEAU pour les workflows récemment terminés -->
                  <span
                    v-if="workflow.isNew && (workflow.status === 'completed' || workflow.status === 'error')"
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-teal-500 text-white animate-pulse flex-shrink-0"
                  >
                    NOUVEAU
                  </span>
                </div>
                <div class="flex items-center space-x-2 mt-1">
                  <span
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                    :class="{
                      'bg-blue-100 text-blue-800': workflow.status === 'processing',
                      'bg-green-100 text-green-800': workflow.status === 'completed' || workflow.status === 'error',
                      'bg-gray-100 text-gray-800': workflow.status === 'pending'
                    }"
                  >
                    {{ getStatusLabel(workflow.status) }}
                  </span>
                  <span class="text-xs text-gray-500">
                    {{ formatDate(workflow.updatedAt) }}
                  </span>
                </div>
                
                <!-- Barre de progression -->
                <div v-if="workflow.status === 'processing'" class="mt-2">
                  <div class="flex items-center justify-between text-xs text-gray-600 mb-1">
                    <span>{{ workflow.progress }}%</span>
                  </div>
                  <div class="w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      class="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                      :style="{ width: `${workflow.progress}%` }"
                    ></div>
                  </div>
                </div>
                
                <!-- Message d'erreur -->
                <!--                 <p v-if="workflow.error" class="text-xs text-red-600 mt-1 truncate" :title="workflow.error">
                  {{ workflow.error }}
                </p> -->
              </div>

              <!-- Bouton supprimer -->
              <button
                v-if="workflow.status === 'completed' || workflow.status === 'error'"
                @click.stop="removeWorkflow(workflow.id)"
                class="flex-shrink-0 text-gray-400 hover:text-teal-600 transition-colors"
                :title="t('workflows.remove')"
              >
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted, onUnmounted } from 'vue';
import { useWorkflowManager } from '~/composables/useWorkflowManager';
import type { WorkflowStatus } from '~/composables/useWorkflowManager';

const { t } = useI18n();
const router = useRouter();
const { workflows, workflowCount, hasActiveWorkflows, removeWorkflow: removeWorkflowFromStore, markWorkflowAsSeen } = useWorkflowManager();

const showDropdown = ref(false);

// Compter les workflows "nouveaux" (récemment terminés)
const newWorkflowsCount = computed(() => {
  return workflows.value.filter((w: any) => w.isNew && (w.status === 'completed' || w.status === 'error')).length;
});

// Watcher pour logger les changements de nouveaux workflows
watch(newWorkflowsCount, (newCount: number, oldCount: number) => {
  if (newCount > oldCount) {
    console.log(`🔔 [WorkflowNotifications] ${newCount} nouveau(x) workflow(s) à notifier`);
  }
});

const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value;
};

const goToWorkflow = (workflowId: string) => {
  // Marquer comme vu
  markWorkflowAsSeen(workflowId);
  showDropdown.value = false;
  router.push(`/agent?requestId=${workflowId}`);
};

const removeWorkflow = (workflowId: string) => {
  removeWorkflowFromStore(workflowId);
};

const getStatusLabel = (status: WorkflowStatus): string => {
  const labels: Record<WorkflowStatus, string> = {
    pending: t('workflows.status.pending'),
    processing: t('workflows.status.processing'),
    completed: t('workflows.status.completed'),
    error: t('workflows.status.completed')
  };
  return labels[status] || status;
};

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return t('workflows.time.now');
  if (minutes < 60) return t('workflows.time.minutesAgo', { count: minutes });
  if (hours < 24) return t('workflows.time.hoursAgo', { count: hours });
  return t('workflows.time.daysAgo', { count: days });
};

// Fermer le dropdown en cliquant à l'extérieur
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (showDropdown.value && !target.closest('.relative')) {
    showDropdown.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

